import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../../contexts/ApiContext';
import MirandaService from '../../service/miranda.service';
import mirandaMark from '../../assets/img/miranda-mark.svg';
import { accountPath } from '../../utils/accountPath';
import { buildUpgradeWhatsappUrl } from '../../utils/planWhatsapp';

const conversationStorageKey = (accountId) => `miranda-ai-conversation:${accountId}`;

function MirandaChatFab({ accountId }) {
  const navigate = useNavigate();
  const { account } = useContext(ApiContext);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!accountId) return;
    try {
      const data = await MirandaService.getStatus(accountId);
      setStatus(data);
    } catch {
      setStatus({ enabled: false, chatAvailable: false });
    }
  }, [accountId]);

  const restoreConversation = useCallback(async () => {
    if (!accountId) return;
    const raw = localStorage.getItem(conversationStorageKey(accountId));
    const savedId = raw ? Number(raw) : null;
    if (!savedId) {
      setConversationId(null);
      setMessages([]);
      return;
    }
    setLoadingConv(true);
    try {
      const data = await MirandaService.getConversation(accountId, savedId);
      setConversationId(data.id);
      setMessages(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        })),
      );
    } catch {
      localStorage.removeItem(conversationStorageKey(accountId));
      setConversationId(null);
      setMessages([]);
    } finally {
      setLoadingConv(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, account?.id]);

  useEffect(() => {
    restoreConversation();
  }, [restoreConversation]);

  useEffect(() => {
    if (!open) return;
    const raw = localStorage.getItem(conversationStorageKey(accountId));
    const savedId = raw ? Number(raw) : null;
    if (savedId !== conversationId) {
      restoreConversation();
    }
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open, restoreConversation, accountId, conversationId]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open, loadingConv]);

  if (!status?.enabled) return null;

  const usage = status.usage || account?.aiChatUsage;
  const usagePct =
    usage?.limit > 0 ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
  const limitReached =
    !!usage?.enabled && usage.limit > 0 && usage.used >= usage.limit;
  const upgradeUrl = buildUpgradeWhatsappUrl({
    accountName: account?.name,
    planName: account?.plan?.name,
    whatsappUpgradeUrl: usage?.whatsappUpgradeUrl,
  });

  const startNewConversation = () => {
    localStorage.removeItem(conversationStorageKey(accountId));
    setConversationId(null);
    setMessages([]);
    setError('');
    setInput('');
  };

  const openHistory = () => {
    setOpen(false);
    navigate(accountPath(accountId, '/miranda?tab=conversas'));
  };

  const pullDailyReport = async () => {
    if (generatingReport || sending) return;
    setError('');
    setGeneratingReport(true);
    try {
      const report = await MirandaService.generateReport(accountId, 1);
      setOpen(false);
      navigate(accountPath(accountId, `/miranda?reportId=${report.id}`));
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(
        Array.isArray(msg)
          ? msg.join(', ')
          : msg || 'Não foi possível gerar o relatório diário.',
      );
    } finally {
      setGeneratingReport(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    if (limitReached) {
      setError('Limite mensal de requests da IA atingido. Faça upgrade pelo WhatsApp.');
      return;
    }

    if (!status.chatAvailable) {
      setError(
        status.chatFeature === false
          ? 'IA avançada com chat não está disponível no seu plano.'
          : 'API key da conta não configurada. Peça ao suporte para habilitar o chat.',
      );
      return;
    }

    setError('');
    setInput('');
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setSending(true);

    try {
      const history = nextMessages
        .slice(0, -1)
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await MirandaService.chat(accountId, text, {
        history,
        conversationId,
      });
      if (res.conversationId) {
        setConversationId(res.conversationId);
        localStorage.setItem(conversationStorageKey(accountId), String(res.conversationId));
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
      await loadStatus();
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.message;
      const code = data?.code || (typeof msg === 'object' ? msg?.code : null);
      const friendly = Array.isArray(msg)
        ? msg.join(', ')
        : typeof msg === 'object'
          ? msg?.message || 'Não foi possível responder agora.'
          : msg || 'Não foi possível responder agora.';

      if (code === 'AI_CHAT_LIMIT_REACHED' || String(friendly).includes('Limite mensal')) {
        setError('Limite mensal de requests da IA atingido.');
        await loadStatus();
      } else {
        setError(friendly);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            code === 'AI_CHAT_LIMIT_REACHED'
              ? 'Você atingiu o limite mensal do chat com IA. Fale no WhatsApp para fazer upgrade.'
              : friendly.includes('sobrecarregado')
                ? friendly
                : 'Desculpe, tive um problema ao processar sua pergunta. Tente novamente em instantes.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{`
        .miranda-fab {
          position: fixed;
          right: 22px;
          bottom: 22px;
          z-index: 1050;
          width: 60px;
          height: 60px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid rgba(124, 58, 237, 0.35);
          background:
            radial-gradient(circle at 30% 25%, rgba(179, 107, 255, 0.35), transparent 55%),
            linear-gradient(145deg, #0b1220 0%, #07111f 100%);
          box-shadow:
            0 0 0 3px rgba(37, 99, 235, 0.15),
            0 12px 32px rgba(6, 182, 212, 0.28);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .miranda-fab:hover {
          transform: translateY(-2px) scale(1.04);
          box-shadow:
            0 0 0 4px rgba(37, 99, 235, 0.22),
            0 16px 36px rgba(109, 40, 217, 0.35);
        }
        .miranda-fab img {
          width: 34px;
          height: 34px;
          display: block;
        }
        .miranda-fab.is-open {
          background: linear-gradient(145deg, #1e293b, #0f172a);
          border-color: rgba(148, 163, 184, 0.35);
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
        }
        .miranda-fab-close {
          color: #f8fafc;
          font-size: 26px;
          font-weight: 300;
          line-height: 1;
        }

        .miranda-panel {
          position: fixed;
          right: 22px;
          bottom: 96px;
          z-index: 1050;
          width: min(400px, calc(100vw - 28px));
          height: min(560px, calc(100vh - 130px));
          background:
            radial-gradient(ellipse at top right, rgba(109, 40, 217, 0.22), transparent 45%),
            radial-gradient(ellipse at bottom left, rgba(6, 182, 212, 0.14), transparent 40%),
            linear-gradient(165deg, #0b1220 0%, #07111f 55%, #0a1628 100%);
          border: 1px solid rgba(124, 58, 237, 0.28);
          border-radius: 18px;
          box-shadow:
            0 0 0 1px rgba(6, 182, 212, 0.08),
            0 22px 55px rgba(0, 0, 0, 0.5);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: mirandaIn 0.22s ease;
        }
        @keyframes mirandaIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .miranda-panel-header {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.12);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(180deg, rgba(37, 99, 235, 0.16), transparent);
        }
        .miranda-panel-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .miranda-panel-brand img {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          flex-shrink: 0;
          box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.35);
        }
        .miranda-panel-header h5 {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          letter-spacing: 0.02em;
          color: #f8fafc !important;
          line-height: 1.2;
        }
        .miranda-panel-header small {
          display: block;
          color: #22d3ee;
          margin-top: 3px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.04em;
        }
        .miranda-header-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }
        .miranda-header-link {
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.04);
          color: #e2e8f0;
          font-size: 11px;
          font-weight: 600;
          padding: 6px 10px;
          cursor: pointer;
          white-space: nowrap;
        }
        .miranda-header-link:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        .miranda-close {
          width: 32px;
          height: 32px;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.04);
          color: #e2e8f0;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .miranda-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .miranda-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .miranda-bubble {
          max-width: 88%;
          padding: 10px 13px;
          border-radius: 14px;
          font-size: 13.5px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .miranda-bubble.user {
          align-self: flex-end;
          background: linear-gradient(135deg, #2563eb, #6d28d9);
          color: #f8fafc;
          border-bottom-right-radius: 5px;
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
        }
        .miranda-bubble.assistant {
          align-self: flex-start;
          background: rgba(248, 250, 252, 0.06);
          color: #e2e8f0;
          border: 1px solid rgba(148, 163, 184, 0.12);
          border-bottom-left-radius: 5px;
        }
        .miranda-empty {
          margin: auto;
          text-align: center;
          color: #94a3b8;
          padding: 24px 16px;
          max-width: 280px;
        }
        .miranda-empty-mark {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 16px;
          box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.3), 0 10px 24px rgba(6, 182, 212, 0.2);
        }
        .miranda-empty strong {
          display: block;
          color: #f8fafc;
          margin-bottom: 8px;
          font-size: 16px;
        }

        .miranda-form {
          border-top: 1px solid rgba(148, 163, 184, 0.12);
          padding: 12px;
          display: flex;
          gap: 8px;
          background: rgba(0, 0, 0, 0.18);
        }
        .miranda-form textarea {
          flex: 1;
          resize: none;
          min-height: 44px;
          max-height: 96px;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.18);
          background: rgba(15, 23, 42, 0.65);
          color: #f8fafc;
          padding: 10px 12px;
          font-size: 13.5px;
        }
        .miranda-form textarea::placeholder {
          color: #64748b;
        }
        .miranda-form textarea:focus {
          outline: none;
          border-color: rgba(34, 211, 238, 0.55);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
        }
        .miranda-send {
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #2563eb, #6d28d9);
          color: #f8fafc;
          padding: 0 16px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3);
        }
        .miranda-send:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
        }
        .miranda-error {
          color: #fda4af;
          font-size: 12px;
          padding: 0 12px 8px;
        }
        .miranda-typing {
          align-self: flex-start;
          color: #94a3b8;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .miranda-typing-dots {
          display: inline-flex;
          gap: 4px;
        }
        .miranda-typing-dots span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22d3ee;
          animation: mirandaDot 1s infinite ease-in-out;
        }
        .miranda-typing-dots span:nth-child(2) { animation-delay: 0.15s; }
        .miranda-typing-dots span:nth-child(3) { animation-delay: 0.3s; }
        @keyframes mirandaDot {
          0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
        .miranda-quick-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          margin-top: 16px;
        }
        .miranda-chip {
          border: 1px solid rgba(124, 58, 237, 0.4);
          background: rgba(124, 58, 237, 0.12);
          color: #e2e8f0;
          border-radius: 999px;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
        }
        .miranda-chip:hover:not(:disabled) {
          background: rgba(124, 58, 237, 0.22);
          border-color: rgba(34, 211, 238, 0.45);
          transform: translateY(-1px);
        }
        .miranda-chip:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .miranda-usage {
          padding: 8px 16px 0;
        }
        .miranda-usage-row {
          display: flex;
          justify-content: space-between;
          color: #94a3b8;
          font-size: 11px;
          margin-bottom: 4px;
        }
        .miranda-usage-bar {
          height: 6px;
          border-radius: 999px;
          background: rgba(148, 163, 184, 0.2);
          overflow: hidden;
        }
        .miranda-usage-bar > span {
          display: block;
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #2563eb, #22d3ee);
        }
        .miranda-usage-bar.is-limit > span {
          background: #ef4444;
        }
        .miranda-upgrade {
          display: block;
          margin: 8px 12px 0;
          text-align: center;
          border-radius: 10px;
          background: #16a34a;
          color: #fff !important;
          font-size: 12px;
          font-weight: 700;
          padding: 10px 12px;
          text-decoration: none;
        }
        .miranda-upgrade:hover {
          background: #15803d;
          color: #fff !important;
        }
      `}</style>

      {open && (
        <div className="miranda-panel" role="dialog" aria-label="Miranda AI">
          <div className="miranda-panel-header">
            <div className="miranda-panel-brand">
              <img src={mirandaMark} alt="" />
              <div>
                <h5>Miranda AI</h5>
                <small>Assistente do PedeGo</small>
              </div>
            </div>
            <div className="miranda-header-actions">
              <button
                type="button"
                className="miranda-header-link"
                onClick={pullDailyReport}
                disabled={generatingReport || sending}
                title="Gera ou atualiza o relatório diário (mesmo do cron das 07h)"
              >
                {generatingReport ? 'Gerando…' : 'Relatório'}
              </button>
              <button type="button" className="miranda-header-link" onClick={startNewConversation}>
                Nova
              </button>
              <button type="button" className="miranda-header-link" onClick={openHistory}>
                Histórico
              </button>
              <button type="button" className="miranda-close" onClick={() => setOpen(false)} aria-label="Fechar">
                ×
              </button>
            </div>
          </div>

          <div className="miranda-messages" ref={listRef}>
            {loadingConv && (
              <div className="miranda-empty">
                <strong>Carregando conversa…</strong>
              </div>
            )}
            {!loadingConv && messages.length === 0 && (
              <div className="miranda-empty">
                <img className="miranda-empty-mark" src={mirandaMark} alt="" />
                <strong>Olá! Sou o Miranda AI.</strong>
                Pergunte sobre o funcionamento do sistema ou a operação da sua conta — pedidos, estoque, cardápio e mais.
                <div className="miranda-quick-actions">
                  <button
                    type="button"
                    className="miranda-chip"
                    onClick={pullDailyReport}
                    disabled={generatingReport || sending}
                  >
                    {generatingReport ? 'Gerando relatório…' : 'Puxar relatório diário'}
                  </button>
                </div>
              </div>
            )}
            {!loadingConv &&
              messages.map((m, idx) => (
                <div key={idx} className={`miranda-bubble ${m.role}`}>
                  {m.content}
                </div>
              ))}
            {(sending || generatingReport) && (
              <div className="miranda-typing">
                <span className="miranda-typing-dots" aria-hidden="true">
                  <span /><span /><span />
                </span>
                {generatingReport ? 'Gerando relatório diário…' : 'Miranda AI está pensando…'}
              </div>
            )}
          </div>

          {usage?.enabled ? (
            <div className="miranda-usage">
              <div className="miranda-usage-row">
                <span>Uso do chat este mês</span>
                <span>
                  {usage.used}/{usage.limit}
                </span>
              </div>
              <div className={`miranda-usage-bar${limitReached ? ' is-limit' : ''}`}>
                <span style={{ width: `${usagePct}%` }} />
              </div>
            </div>
          ) : null}

          {limitReached ? (
            <a
              className="miranda-upgrade"
              href={upgradeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Falar no WhatsApp para upgrade
            </a>
          ) : null}

          {error ? <div className="miranda-error">{error}</div> : null}

          <form className="miranda-form" onSubmit={send}>
            <textarea
              ref={inputRef}
              value={input}
              disabled={sending || !status.chatAvailable || limitReached}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                limitReached
                  ? 'Limite mensal atingido'
                  : status.chatAvailable
                    ? 'Escreva sua dúvida…'
                    : 'Chat indisponível — API key não configurada'
              }
              rows={2}
            />
            <button
              className="miranda-send"
              type="submit"
              disabled={sending || !input.trim() || !status.chatAvailable || limitReached}
            >
              Enviar
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className={`miranda-fab${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fechar Miranda AI' : 'Abrir Miranda AI'}
        title="Miranda AI"
      >
        {open ? (
          <span className="miranda-fab-close" aria-hidden="true">×</span>
        ) : (
          <img src={mirandaMark} alt="" />
        )}
      </button>
    </>
  );
}

export default MirandaChatFab;
